<template>
  <VNavigationDrawer
    location="left"
    mobile-breakpoint="sm"
    v-model="drawer"
    class="node-sidemenu"
    :class="{
      'node-sidemenu--ios': this.$vuetify.display.platform.ios,
    }"
    :width="drawerWidth"
    ref="drawerElement"
    data-cy="nodeSideMenu-menu"
  >
    <div class="d-flex flex-row">
      <div class="d-flex flex-column justify-space-between px-1 w-100 h-100">
        <div :class="{
          'node-sidemenu--ios-menu': this.$vuetify.display.platform.ios,
        }">
          <div class="d-flex">
            <v-text-field
              v-model="searchText"
              prepend-inner-icon="mdi-magnify"
              :label="$t('menus.nodeList.findDirectory')"
              density="comfortable"
              @keydown.enter="findNodes()"
              @click:prepend-inner="findNodes()"
              @click:clear="clearSearchResults()"
              @update:model-value="searchInputChanged()"
              hide-details
              variant="solo-filled"
              flat
              clearable
            />
            <CreateNodesMenu v-if="$canControlNodeAndPod()"/>
          </div>

          <div class="mt-4">
            <AccesspointSwitch/>
          </div>

          <div class="mt-4">
            <NodeExpansionPanel/>
          </div>

        </div>
        <div v-if="showingUpgradeButton" class="d-flex flex-column align-center">
          <div class="d-flex flex-column text-center mb-3">
            <span v-if="getSubscriptionDaysLeft">
              {{ $t('layouts.main.subscriptionDaysLeft', getSubscriptionDaysLeft) }}
            </span>
            <VBtn color="primary" elevation=0 @click="openSubscriptionContactDialog">
              <VIcon>mdi-crown-circle</VIcon>
              {{ $t('layouts.main.upgradeSubscription') }}
            </VBtn>
          </div>
        </div>
      </div>
      <div
        v-if="this.$vuetify.display.smAndUp"
        @mousedown.left.stop.prevent="onResizeBarMouseDown"
        @touchstart.stop.prevent="onResizeBarTouchStart"
        @click.right.prevent
        :class="{ 'resize-handle': true, 'resize-handle--dragging': isDraggingResizeHandle }"
      >
        <v-icon class="resize-handle-icon" icon="mdi-drag-vertical-variant" size="14"/>
      </div>
    </div>

  </VNavigationDrawer>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useMainStore } from '@/stores/MainStore.js'
import { useNodeStore } from '@/stores/NodeStore.js'
import { useSearchStore } from '@/stores/SearchStore.js'
import AccesspointSwitch from '../../../components/AccesspointSwitch.vue'
import CreateNodesMenu from './components/CreateNodesMenu.vue'
import NodeExpansionPanel from './NodeExpansionPanel.vue'
import _ from 'lodash';

const DRAWER_MIN_WIDTH = 290;
const getDrawerMaxWidth = () =>  Math.max(DRAWER_MIN_WIDTH, Math.floor(document.getRootNode().documentElement.clientWidth * 0.5));

export default {
  name: "NodeSidemenu",
  components: {
    AccesspointSwitch,
    CreateNodesMenu,
    NodeExpansionPanel,
  },
  data() {
    const mainStore = useMainStore();

    return {
      drawer: null,
      searchText: '',
      drawerWidth: mainStore.nodeSideMenuWidth > -1 ? mainStore.nodeSideMenuWidth : DRAWER_MIN_WIDTH,
      drawerMaxWidth: getDrawerMaxWidth(),
      isDraggingResizeHandle: false,
      throttledOnWindowResize: null,
      throttledOnMouseMove: null,
      throttledOnTouchMove: null,
    };
  },
  mounted() {
    this.throttledOnWindowResize = _.throttle(this.onWindowResize.bind(this), 100);
    this.throttledOnMouseMove = _.throttle(this.onResizeBarMouseMove.bind(this), 50);
    this.throttledOnTouchMove = _.throttle(this.onResizeBarTouchMove.bind(this), 50);

    window.addEventListener('resize', this.throttledOnWindowResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.throttledOnWindowResize);
    window.removeEventListener('mousemove', this.throttledOnMouseMove);
    window.removeEventListener('touchmove', this.throttledOnTouchMove);
  },
  watch: {
    getSidemenuToggle() {
      this.drawer = !this.drawer;
    },
  },
  methods: {
    openSubscriptionContactDialog() {
      const mainStore = useMainStore();
      mainStore.showSubscriptionContactDialog();
    },
    findNodes() {
      if (this.searchText.length < 3) {
        return;
      }

      if (!this.getCurrentNode || !this.getCurrentNode.id) {
        return;
      }

      const payload = {
        allWords: 1,
        includeAssets: 0,
        includeSubfolders: 1,
        nodeid: this.getCurrentNode.id,
        searchStr: this.searchText,
        searchZone: "folders",
        wholeWords: 0,
      };

      const searchStore = useSearchStore();
      searchStore.searchInFolders(payload);
    },
    clearSearchResults() {
      const searchStore = useSearchStore();
      searchStore.resetFolderSearchResults();
    },
    searchInputChanged() {
      if (!this.searchText) {
        this.clearSearchResults();
      }
    },
    onWindowResize() {
      this.drawerMaxWidth = getDrawerMaxWidth();

      if (this.drawerWidth > this.drawerMaxWidth) {
        this.drawerWidth = this.drawerMaxWidth;

        const mainStore = useMainStore();
        mainStore.setNodeSideMenuWidth(this.drawerWidth);
      }
    },
    onResizeBarTouchStart() {
      this.isDraggingResizeHandle = true;

      window.addEventListener('touchmove', this.throttledOnTouchMove);
      window.addEventListener('touchend', () => {
        window.removeEventListener('touchmove', this.throttledOnTouchMove);
        this.isDraggingResizeHandle = false;

        const mainStore = useMainStore();
        mainStore.setNodeSideMenuWidth(this.drawerWidth);
      }, { once: true });
    },
    onResizeBarMouseDown() {
      this.isDraggingResizeHandle = true;

      document.body.classList.add('node-sidemenu-resize-handle--dragging');

      window.addEventListener('mousemove', this.throttledOnMouseMove);
      window.addEventListener('mouseup', () => {
        window.removeEventListener('mousemove', this.throttledOnMouseMove);

        document.body.classList.remove('node-sidemenu-resize-handle--dragging');
        this.isDraggingResizeHandle = false;

        const mainStore = useMainStore();
        mainStore.setNodeSideMenuWidth(this.drawerWidth);
      }, { once: true });
    },
    /**
     *
     * @param {MouseEvent} event
     */
    onResizeBarMouseMove(event) {
      event.stopPropagation();
      event.preventDefault();

      if (event.clientX < DRAWER_MIN_WIDTH) {
        this.drawerWidth = DRAWER_MIN_WIDTH;
      } else {
        this.drawerWidth = Math.min(this.drawerMaxWidth, event.clientX);
      }
    },
    onResizeBarTouchMove(event) {
      const touch = event.touches[0];

      if (touch.clientX < DRAWER_MIN_WIDTH) {
        this.drawerWidth = DRAWER_MIN_WIDTH;
      } else {
        this.drawerWidth = Math.min(this.drawerMaxWidth, touch.clientX);
      }
    },
  },
  computed: {
    getSidemenuToggle() {
      const mainStore = useMainStore();
      return mainStore.getSidemenuToggle;
    },
    activeTenant() {
      const authStore = useAuthStore();
      return authStore.getActiveTenant;
    },
    showingUpgradeButton() {
      return  this.$canControlTenantConfig() &&
              !this.$isFCPExtension() &&
              !this.$isAdobeExtension() &&
              this.activeTenant &&
              this.activeTenant.subscription &&
              this.activeTenant.subscription.subscriptionType &&
              this.activeTenant.subscription.subscriptionType === 'freetrial';
    },
    getSubscriptionDaysLeft() {
      if (this.activeTenant?.subscription?.subscriptionEnd) {
        return this.activeTenant?.subscription?.subscriptionEnd?.days || 0;
      }

      return 0;
    },
    getCurrentNode() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
  }
}
</script>

<style lang="scss" scoped>
.node-sidemenu {
  position: fixed !important;
  top: 0 !important;
  bottom: 0 !important;
  margin-top: 80px;
  margin-left: 4px;
  padding: 8px 16px 8px 8px;
  max-height: calc(100% - 84px) !important;
  border-radius: 7px;

  &--ios {
    padding-right: 0;
  }

  &--ios-menu {
    width: calc(100% - 16px);
  }

  .resize-handle {
    position: absolute;
    right: 0;
    height: calc(100% - 16px); // Compensate for the additional padding, introduced with .node-sidemenu
    cursor: col-resize;
    border-radius: 7px;
    margin-right: 4px;

    &-icon {
      top: 50%;
    }

    &--dragging {
      background-color: rgb(var(--v-theme-secondaryButtons));
    }
  }

  &.v-theme--dark {
    background-color: #232b36 !important;
  }

  &.v-theme--light {
    background-color: #FFFFFF !important
  }

  :deep(.v-navigation-drawer__content) {
    overflow-x: auto;
  }

  @media only screen and (max-width: 600px) {
    & {
      margin-top: 80px;
      margin-left: 0;
      border-radius: 0;
      max-height: calc(100% - 84px) !important;
    }

    &.v-navigation-drawer--active {
      width: 100vw !important;
    }
  }
}

:global(body.node-sidemenu-resize-handle--dragging) {
  cursor: col-resize;
}
</style>
